import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import AllInOneFooter from "components/allInOneDashboard/AllInOneFooter";
import OnestockPricingSection from "components/Product-Info-Pages/Onestock/OnestockPricingSection";
import ProductInfoOfferBanner from "components/Product-Info-Pages/ProductInfoOfferBanner";
import { UNIFIED_DASHBOARD_PATH } from "components/allInOneDashboard/constants/productRouteMap";
import { config } from "config/index";
import { useProductDiscountOffer } from "hooks/useProductDiscountOffer";
import { jarvsiOnestockOnboardingActions } from "stores/jarvisOnestock/jarvisOnestockOnboarding/jarvisOnestockOnboardingAction";
import { thirdpartyApis } from "stores/thirdparty/thirdpartyApis";
import { thirdpartyParsers } from "stores/thirdparty/thirdpartyParsers";
import { localStore } from "stores/localStorage";

const ONBOARDING_PATH = "/jarvis-onestock/onboarding/overview";

const ACCENT = "#3E80E5";
const FACT_DOT = "#4084EE";
const YEAR_CARD_BG = "#83B2FC";
const HERO_GRADIENT_WEB =
  "linear-gradient(162deg, #0B1020 1.5%, #0F1B4D 55%, #1D4ED8 98.5%)";
const HERO_GRADIENT_MOBILE = "linear-gradient(136deg, #030033 1.5%, #337DF0 98.5%)";
const CTA_GRADIENT = "linear-gradient(270deg, #1C4CD3 0%, #101D54 100%)";
const CTA_GRADIENT_MOBILE = "linear-gradient(283deg, #1160DF 0.6%, #021091 99%)";
const FOR_YOU_GRADIENT = "linear-gradient(134deg, #171545 0%, #4084EE 100%)";
const FEATURE_BORDER = "#E5E7EB";
const FOR_YOU_BORDER = "rgba(62,128,229,0.12)";
const FACT_BORDER = "#E2E8F0";
const ABOUT_COLLAPSED_HEIGHT = 115;

const img = (name) => `/images/onestock-product-info/${encodeURIComponent(name)}`;

const ICONS = {
  hero: img("onestock.png"),
  frame: img("Frame.svg"),
  frameWhite: img("Frame-white.svg"),
  check: img("icon.svg"),
  received: img("received 1.svg"),
  idea: img("idea 1.svg"),
  support: img("24-hours-support 1.svg"),
  noStopping: img("no-stopping 1.svg"),
};

const RECEIVE_TIMELY = [
  "Buy Price/ Entry",
  "Buy Range",
  "Target",
  "Stop Loss",
  "Rationale",
  "Holding Period",
  "Buy/ Sell Recommendation",
  "Profit Booking/ Exit Updates",
];

const FEATURE_CARDS = [
  {
    icon: ICONS.idea,
    text: "Identifies high conviction buy & sell opportunities using AI driven stock selection.",
  },
  {
    icon: ICONS.support,
    text: "24 x 7 AI powered monitoring keeps every recommendation under continuous review.",
  },
  {
    icon: ICONS.noStopping,
    text: "Invest for 1 - 45 days with no minimum investment requirement.",
  },
];

const FOR_YOU_CARDS = [
  { label: "Short Term", description: "Looking for short term profit booking?" },
  { label: "Daily stock ideas", description: "Want daily AI powered stock ideas?" },
  {
    label: "Timely entry & exit",
    description: "Prefer timely entry & exit recommendations with rationale",
  },
  {
    label: "Stock picks",
    description: "Looking for expert backed, data driven stock pcks",
  },
  {
    label: "Invest without spending hours",
    description: "Invest before spending hours tracking the markets",
  },
  {
    label: "Disciplined investing",
    description: "Built for investors seeking disciplined short term investing",
  },
];

const QUICK_FACTS = [
  { label: "Min Capital", value: "-" },
  { label: "Horizon", value: "1 - 45 Days" },
  { label: "Market Coverage", value: "NSE" },
  { label: "Stock Type", value: "Short Term" },
  { label: "Alerts", value: "Buy & Sell" },
  { label: "Support", value: "Chat & Email" },
];

const Bold = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 600 }}>
    {children}
  </Box>
);

const MOBILE_ABOUT_ITEMS = [
  {
    icon: ICONS.frame,
    text: (
      <>
        <Bold>AI engine live</Bold> in Indian Market since <Bold>2016</Bold>
      </>
    ),
  },
  {
    icon: ICONS.idea,
    text: (
      <>
        Identifies high-conviction <Bold>buy & Sell opportunities</Bold> using AI-driven stock
        selection.
      </>
    ),
  },
  {
    icon: ICONS.support,
    text: "24×7 AI-powered monitoring keeps every recommendation under continuous review.",
  },
  {
    icon: ICONS.noStopping,
    text: "Invest for 1–45 days with no minimum investment requirement.",
  },
  {
    icon: ICONS.received,
    isReceiveTimely: true,
  },
];

const MOBILE_FOR_YOU = [
  "Looking for short term profit opportunities?",
  "Want daily AI powered stock ideas?",
  "Prefer timely entry and exit recommendations with rationale",
  "Looking for expert-backed, data-driven stock picks",
  "Invest without spending hours tracking the markets.",
  "Built for investors seeking disciplined short-term investing.",
];

const MOBILE_QUICK_FACTS = [
  { label: "Min Capital", value: "-" },
  { label: "Horizon", value: "1-45 Days" },
  { label: "Market Coverage", value: "NSE" },
  { label: "Stock Type", value: "Short Term" },
  { label: "Alerts", value: "Buy & Sell" },
  { label: "Support", value: "Chat & Email" },
];

const MOBILE_RECEIVE_TIMELY = [
  "Buy Price/ Entry",
  "Buy Range",
  "Target",
  "Stop Loss",
  "Rationale",
  "Holding Period",
  "Buy/Sell Recommendation",
  "Profit Booking / Exit Updates",
];

const pagePad = {
  px: { xs: 2.5, md: 7.5 },
  maxWidth: 1320,
  mx: "auto",
  width: "100%",
};

const FeatureCard = ({ icon, text, isReceiveTimely }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 4,
      height: "100%",
      borderRadius: "20px",
      bgcolor: "#fff",
      border: `1px solid ${FEATURE_BORDER}`,
      boxShadow: "0 6px 9px rgba(0,0,0,0.05)",
    }}
  >
    <Box component="img" src={icon} alt="" sx={{ width: 32, height: 32 }} />
    {isReceiveTimely ? (
      <Box>
        <Text sx={{ fontSize: 18, fontWeight: 500, color: "#111827", lineHeight: "26px", mb: 0 }}>
          Receive Timely:
        </Text>
        <Box component="ul" sx={{ m: 0, pl: "27px", listStyleType: "disc" }}>
          {RECEIVE_TIMELY.map((item) => (
            <Box
              component="li"
              key={item}
              sx={{ fontSize: 18, fontWeight: 500, color: "#111827", lineHeight: "26px" }}
            >
              {item}
            </Box>
          ))}
        </Box>
      </Box>
    ) : (
      <Text sx={{ fontSize: 18, fontWeight: 500, color: "#111827", lineHeight: "26px" }}>
        {text}
      </Text>
    )}
  </Stack>
);

const ChecklistCard = ({ label, description }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: "12px",
      bgcolor: "#fff",
      border: `1px solid ${FOR_YOU_BORDER}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        bgcolor: ACCENT,
      }}
    />
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: "12px",
          bgcolor: "rgba(62,128,229,0.05)",
          border: "1px solid rgba(62,128,229,0.2)",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box component="img" src={ICONS.check} alt="" sx={{ width: 18, height: 18 }} />
      </Box>
      <Text sx={{ fontSize: 15, fontWeight: 600, color: "#374151", lineHeight: "20px" }}>
        {label}
      </Text>
    </Stack>
    <Text sx={{ fontSize: 13, color: "#313131", lineHeight: "20px" }}>{description}</Text>
  </Stack>
);

const FactCard = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      p: 3,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: `1px solid ${FACT_BORDER}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
      <Text sx={{ fontSize: 18, color: "#6B7280" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 24, fontWeight: 700, color: "#111827" }}>{value}</Text>
  </Stack>
);

const MobileAboutRow = ({ icon, text, isReceiveTimely }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box
      component="img"
      src={icon}
      alt=""
      sx={{ width: 20, height: 20, flexShrink: 0, mt: "2px" }}
    />
    {isReceiveTimely ? (
      <Box sx={{ color: "#475569", fontSize: 14, lineHeight: "21px" }}>
        <Text sx={{ fontSize: 14, fontWeight: 500, color: "#475569", lineHeight: "21px", mb: 0 }}>
          Receive Timely:
        </Text>
        {MOBILE_RECEIVE_TIMELY.map((line) => (
          <Text key={line} sx={{ fontSize: 14, color: "#475569", lineHeight: "21px", display: "block" }}>
            {line}
          </Text>
        ))}
      </Box>
    ) : (
      <Text sx={{ fontSize: 14, color: "#475569", lineHeight: "21px" }}>{text}</Text>
    )}
  </Stack>
);

const MobileForYouRow = ({ text }) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    sx={{
      p: 2,
      minHeight: 63,
      borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 10px 12px rgba(0,0,0,0.1)",
      backdropFilter: "blur(7px)",
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
    </Box>
    <Text sx={{ fontSize: 14, fontWeight: 500, color: "#fff", lineHeight: 1.35 }}>{text}</Text>
  </Stack>
);

const MobileFactTile = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      flex: 1,
      minWidth: 0,
      minHeight: 90,
      p: 2,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: `1px solid ${FACT_BORDER}`,
      boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
      <Text sx={{ fontSize: 12, color: "#000", lineHeight: "16px" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
      {value}
    </Text>
  </Stack>
);

const OnestockProductInfo = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const { offer, isActive } = useProductDiscountOffer(
    config.productCodes.ONESTOCK,
  );

  useEffect(() => {
    dispatch(jarvsiOnestockOnboardingActions.getOnboardingData());
    setPlansLoading(true);
    thirdpartyApis
      .getSubscriptionPlans({ productCode: config.productCodes.ONESTOCK })
      .then((res) => setPlans(thirdpartyParsers.subscriptionPlans(res) || []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, [dispatch]);

  const handleStart = () => {
    localStore.setActiveProduct(config.productCodes.ONESTOCK);
    history.push(ONBOARDING_PATH);
  };

  const handleBack = () => {
    history.push(UNIFIED_DASHBOARD_PATH);
  };

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      {/* Hero */}
      <Box
        sx={{
          background: { xs: HERO_GRADIENT_MOBILE, md: HERO_GRADIENT_WEB },
          px: { xs: 3, md: 7.5 },
          pt: { xs: 2.5, md: 4 },
          pb: { xs: 0, md: 10 },
          overflow: "hidden",
          borderRadius: { xs: "0 0 24px 24px", md: 0 },
          boxShadow: {
            xs: "0 12px 28px -10px rgba(99,23,176,0.1)",
            md: "none",
          },
        }}
      >
        <Box sx={{ maxWidth: 1320, mx: "auto" }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: { xs: 2.5, md: 6 } }}
          >
            <IconButton
              onClick={handleBack}
              aria-label="Back to Explore"
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.2)",
                border: "1px solid #C3C3C3",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                p: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: { xs: 20, md: 24 }, color: "#fff" }} />
            </IconButton>
            <Stack spacing={{ xs: 0.25, md: 1.1 }}>
              <Text
                sx={{
                  fontSize: { xs: 18, md: 32 },
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: { xs: "24px", md: "26px" },
                }}
              >
                Jarvis One Stock
              </Text>
              <Text
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: { xs: "21px", md: "20px" },
                }}
              >
                Investing powered by AI
              </Text>
            </Stack>
          </Stack>

          {/* Desktop hero */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={4}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            <Stack spacing={3} sx={{ maxWidth: 624 }}>
              <Text
                sx={{
                  fontSize: 64,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "84px",
                }}
              >
                AI-Driven{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(90deg, #60A5FA 0%, #3B82F6 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Stock Recommendation
                </Box>
              </Text>
              <Text
                sx={{
                  fontSize: 20,
                  color: "rgba(255,255,255,0.72)",
                  lineHeight: "30px",
                }}
              >
                Jarvis One Stock helps you identify short-term market opportunities with
                AI-powered stock recommendations and timely entry & exit calls to help you book
                profits with confidence.
              </Text>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mt: 2, flexWrap: "wrap" }}
              >
                <Button
                  onClick={handleStart}
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    flexShrink: 0,
                    px: 4,
                    py: 2.25,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 16,
                    color: "#fff",
                    background: CTA_GRADIENT,
                    border: "1px solid rgba(255,255,255,0.3)",
                    boxShadow: "0 10px 12px rgba(28,75,208,0.5)",
                    "&:hover": { background: CTA_GRADIENT, opacity: 0.95 },
                  }}
                >
                  Start Investing
                </Button>
              </Stack>
            </Stack>
            <Box
              component="img"
              src={ICONS.hero}
              alt="Jarvis One Stock"
              sx={{ width: 399, maxWidth: "40%", objectFit: "contain" }}
            />
          </Stack>

          {/* Mobile hero — Figma 6959:44775 */}
          <Stack spacing={1.25} sx={{ display: { xs: "flex", md: "none" } }}>
            <Text
              sx={{
                fontSize: 30,
                fontWeight: 800,
                color: "#fff",
                lineHeight: "38px",
              }}
            >
              AI-Driven Stock Recommendations
            </Text>
            <Text
              sx={{
                fontSize: 14,
                color: "rgba(255,255,255,0.72)",
                lineHeight: "21px",
              }}
            >
              Jarvis One Stock helps you identify short-term market opportunities with AI-powered
              stock recommendations and timely entry & exit calls to help you book profits with
              confidence.
            </Text>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 1.5,
                mt: 5,
                minHeight: 200,
              }}
            >
              <Box
                component="img"
                src={ICONS.hero}
                alt="Jarvis One Stock"
                sx={{
                  width: 201,
                  height: 209,
                  maxWidth: "55%",
                  objectFit: "cover",
                  objectPosition: "center top",
                  alignSelf: "flex-end",
                  ml: "-11px",
                  mb: "-20px",
                }}
              />
              <Button
                onClick={handleStart}
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 24 }} />}
                sx={{
                  flexShrink: 0,
                  mb: 2.5,
                  height: 48,
                  px: 2,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  whiteSpace: "nowrap",
                  background: CTA_GRADIENT_MOBILE,
                  border: "1px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 10px 12px rgba(37,99,235,0.2)",
                  "&:hover": { background: CTA_GRADIENT_MOBILE, opacity: 0.95 },
                }}
              >
                Start Investing
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>
      <ProductInfoOfferBanner
        offer={offer}
        isActive={isActive}
        sx={{ ...pagePad, pt: { xs: 2.5, md: 3 }, pb: 0 }}
      />

      {/* About One Stock — desktop */}
      <Box sx={{ ...pagePad, py: 7.5, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 56,
            fontWeight: 700,
            color: "#111827",
            letterSpacing: "-0.02em",
            lineHeight: "64px",
            mb: 6,
          }}
        >
          About One Stock
        </Text>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 4,
              borderRadius: "20px",
              bgcolor: YEAR_CARD_BG,
              boxShadow: "0 10px 12px rgba(0,0,0,0.1)",
              minHeight: 320,
              position: "relative",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box component="img" src={ICONS.frameWhite} alt="" sx={{ width: 32, height: 32 }} />
              <Text
                sx={{
                  bgcolor: "#fff",
                  px: 2,
                  py: 0.5,
                  borderRadius: "14px",
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#1D275F",
                }}
              >
                Currently AI under reinforcement stage
              </Text>
            </Stack>
            <Text
              sx={{
                mt: 2.5,
                fontSize: 96,
                fontWeight: 700,
                color: "#fff",
                lineHeight: "96px",
                letterSpacing: "-0.02em",
              }}
            >
              2016
            </Text>
            <Text sx={{ mt: 1.25, fontSize: 24, fontWeight: 500, color: "rgba(255,255,255,0.9)" }}>
              AI Engine live in Indian Market since
            </Text>
          </Box>
          <FeatureCard icon={ICONS.received} isReceiveTimely />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
          }}
        >
          {FEATURE_CARDS.map((item) => (
            <FeatureCard key={item.text} {...item} />
          ))}
        </Box>
      </Box>

      {/* About One Stock — mobile Figma 7010:46602 */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2}
          sx={{
            p: 2.5,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
            About One Stock
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Box
            sx={{
              maxHeight: aboutExpanded ? "none" : ABOUT_COLLAPSED_HEIGHT,
              overflow: "hidden",
            }}
          >
            <Stack spacing={2.5}>
              {MOBILE_ABOUT_ITEMS.map((item, index) => (
                <MobileAboutRow key={`about-${index}`} {...item} />
              ))}
            </Stack>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setAboutExpanded((v) => !v)}
            sx={{
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              p: 0,
              alignSelf: "flex-start",
            }}
          >
            <Text sx={{ fontSize: 14, fontWeight: 500, color: "#2977EC", lineHeight: "24px" }}>
              {aboutExpanded ? "read less" : "read more"}
            </Text>
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — desktop */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          py: 12,
          px: 10,
          bgcolor: "#fff",
          position: "relative",
          overflow: "hidden",
          backgroundImage: `
            linear-gradient(rgba(62,128,229,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(62,128,229,0.06) 1px, transparent 1px)
          `,
          backgroundSize: "240px 180px",
        }}
      >
        <Stack
          direction="row"
          spacing={8}
          alignItems="center"
          sx={{ maxWidth: 1280, mx: "auto", position: "relative" }}
        >
          <Stack spacing={4} sx={{ flex: "0 1 520px" }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "36px",
                border: `1.5px solid ${ACCENT}`,
                display: "grid",
                placeItems: "center",
                bgcolor: "#fff",
                fontSize: 40,
                fontWeight: 800,
                color: ACCENT,
                boxShadow: "0 10px 12px rgba(0,0,0,0.04)",
              }}
            >
              ?
            </Box>
            <Text
              sx={{
                fontSize: 80,
                fontWeight: 900,
                color: "#1A1A1A",
                lineHeight: "88px",
                letterSpacing: "-1.6px",
              }}
            >
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 18, color: "#6B7280", lineHeight: "28px" }}>
              A quick checklist to help you decide if our AI-driven portfolio management is the
              right fit for your financial goals.
            </Text>
          </Stack>

          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              width: "100%",
            }}
          >
            {FOR_YOU_CARDS.map((card) => (
              <ChecklistCard key={card.label} {...card} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — mobile */}
      <Box sx={{ px: 3, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "24px",
            overflow: "hidden",
            position: "relative",
            border: "1px solid rgba(255,255,255,0.15)",
            background: FOR_YOU_GRADIENT,
            boxShadow:
              "0 0 18px rgba(64,132,238,0.15), 0 10px 24px -10px rgba(23,21,69,0.3), 0 18px 40px -12px rgba(0,0,0,0.15)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -110,
              right: -90,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(37,67,143,0.55) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -120,
              right: 40,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(37,67,143,0.45) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Stack spacing={1.25} sx={{ position: "relative" }}>
            <Text sx={{ fontSize: 18, fontWeight: 700, color: "#E6E6E6", lineHeight: "24px" }}>
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: "18px" }}>
              A quick checklist to help you decide if this is the right fit.
            </Text>
          </Stack>
          <Box sx={{ borderBottom: "1px solid rgba(255,255,255,0.2)", position: "relative" }} />
          <Stack spacing={1.5} sx={{ position: "relative" }}>
            {MOBILE_FOR_YOU.map((text) => (
              <MobileForYouRow key={text} text={text} />
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* Quick Facts — desktop */}
      <Box sx={{ ...pagePad, py: 7.5, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 36,
            fontWeight: 700,
            color: "#111827",
            mb: 5,
          }}
        >
          Quick Facts
        </Text>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2.5,
          }}
        >
          {QUICK_FACTS.map((fact) => (
            <FactCard key={`${fact.label}-${fact.value}`} {...fact} />
          ))}
        </Box>
      </Box>

      {/* Quick Facts — mobile */}
      <Box sx={{ px: 3, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "26px" }}>
            Quick Facts
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            {MOBILE_QUICK_FACTS.map((fact) => (
              <MobileFactTile key={`${fact.label}-${fact.value}`} {...fact} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Pricing */}
      <Box
        sx={{
          px: { xs: 2, md: 10 },
          py: { xs: 1.25, md: 15 },
          bgcolor: { xs: "#fff", md: "#F8FAFC" },
          maxWidth: "100%",
        }}
      >
        <Box sx={{ maxWidth: 1320, mx: "auto", width: "100%" }}>
          <Box sx={{ display: { xs: "none", md: "block" }, mb: 3 }}>
            <Stack spacing={1.5} alignItems="center">
              <Text
                sx={{
                  textAlign: "center",
                  fontSize: 48,
                  fontWeight: 800,
                  color: "#0F172A",
                }}
              >
                Pricings
              </Text>
              <Text
                sx={{
                  textAlign: "center",
                  fontSize: 20,
                  color: "#475569",
                }}
              >
                Choose the plan that fits your investing style
              </Text>
            </Stack>
          </Box>
          <OnestockPricingSection
            plans={plans}
            loading={plansLoading}
            onUnlock={handleStart}
          />
        </Box>
      </Box>

      <AllInOneFooter />
    </Box>
  );
};

export default OnestockProductInfo;
